import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

const TYPE_OPTIONS   = ['Machine', 'Labour', 'Other'];
const GROUP_OPTIONS  = ['Resources', 'Labour', 'Machines', 'Tooling', 'Overhead'];
const ISSUE_OPTIONS  = ['Manual', 'Backflush'];
const ALLOC_OPTIONS  = ['None', 'On Start Date', 'On End Date', 'On Order Date'];

const TABS = [
  { key: 'general',    label: 'General' },
  { key: 'capacity',   label: 'Capacity Data' },
  { key: 'planning',   label: 'Planning Data' },
  { key: 'fixedAssets', label: 'Fixed Assets' },
  { key: 'properties', label: 'Properties' },
  { key: 'attachments', label: 'Attachments' },
  { key: 'remarks',    label: 'Remarks' },
];

export default function ResourceDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [loading, setLoading]    = useState(!isNew);
  const [saving, setSaving]      = useState(false);
  const [error, setError]        = useState('');
  const [tab, setTab]            = useState('general');

  const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

  const emptyForm = {
    resource_no: '', bar_code: '', description: '', foreign_name: '',
    resource_type: 'Machine', resource_group: 'Resources',
    unit_of_measure: '', time_per_unit: '0:00:00', units_per_period: 1,
    issue_method: 'Manual', resource_allocation: 'None', linked_to_item: '',
    status: 'Active', remarks: '',
  };
  const emptyCosts = Array.from({ length: 10 }, (_, i) => ({ cost_number: i + 1, cost_amount: 0 }));
  const emptyCapacity = DAYS.map(d => ({ day: d, f1: '', f2: '', f3: '', f4: '', daily: '' }));
  const emptyWarehouses = [];
  const emptyFixedAssets = [];
  const emptyProperties = [];
  const emptyAttachments = [];

  const [form, setForm]   = useState(emptyForm);
  const [costs, setCosts] = useState(emptyCosts);
  const [capacity, setCapacity] = useState(emptyCapacity);
  const [stdCapacity, setStdCapacity] = useState({ f1: '', f2: '', f3: '', f4: '' });
  const [warehouses, setWarehouses]   = useState(emptyWarehouses);
  const [fixedAssets, setFixedAssets] = useState(emptyFixedAssets);
  const [properties, setProperties]   = useState(emptyProperties);
  const [attachments, setAttachments] = useState(emptyAttachments);
  const [capacityFrom, setCapacityFrom] = useState('');
  const [capacityTo, setCapacityTo]     = useState('');

  useEffect(() => {
    if (isNew) {
      (async () => {
        try {
          const n = await api.get('/resources/next-number');
          setForm(f => ({ ...f, resource_no: n.next }));
        } catch {}
      })();
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const d = await api.get(`/resources/${id}`);
        setForm({
          resource_no: d.resource_no || '',
          bar_code: d.bar_code || '',
          description: d.description || '',
          foreign_name: d.foreign_name || '',
          resource_type: d.resource_type || 'Machine',
          resource_group: d.resource_group || 'Resources',
          unit_of_measure: d.unit_of_measure || '',
          time_per_unit: d.time_per_unit || '0:00:00',
          units_per_period: d.units_per_period ?? 1,
          issue_method: d.issue_method || 'Manual',
          resource_allocation: d.resource_allocation || 'None',
          linked_to_item: d.linked_to_item || '',
          status: d.status || 'Active',
          remarks: d.remarks || '',
        });
        if (Array.isArray(d.costs) && d.costs.length) {
          const mapped = emptyCosts.map(c => {
            const found = d.costs.find(x => x.cost_number === c.cost_number);
            return found ? { cost_number: c.cost_number, cost_amount: Number(found.cost_amount) || 0 } : c;
          });
          setCosts(mapped);
        }
      } catch { setError('Failed to load resource'); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const totalStdCost = costs.reduce((s, c) => s + (Number(c.cost_amount) || 0), 0);

  const save = async () => {
    if (!form.resource_no || !form.description) { setError('Resource No. and Description are required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        units_per_period: Number(form.units_per_period) || 1,
        costs: costs.filter(c => Number(c.cost_amount) > 0),
      };
      if (isNew) {
        const result = await api.post('/resources', payload);
        navigate(`/resource/${result.resource_id}`, { replace: true });
      } else {
        await api.put(`/resources/${id}`, payload);
        const d = await api.get(`/resources/${id}`);
        setForm(f => ({ ...f, resource_no: d.resource_no }));
      }
    } catch (e) { setError(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteResource = async () => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await api.del(`/resources/${id}`);
      navigate('/resources/master');
    } catch (e) { setError(e.message || 'Cannot delete'); }
  };

  if (loading) return <div className="p-6 text-center text-slate-400">Loading…</div>;

  const fieldLabel = 'text-xs font-semibold text-slate-600 whitespace-nowrap';
  const fieldInput = 'w-full px-2 py-1.5 rounded border border-slate-300 text-sm focus:ring-1 focus:ring-sap-blue/40 focus:outline-none';
  const fieldSelect = fieldInput + ' bg-white';

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {/* Title bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/resources/master')} className="text-sm text-sap-blueDeeper hover:underline">← Back</button>
          <h1 className="text-lg font-bold text-[#1A3C34]">Resource Master Data</h1>
        </div>
        {!isNew && (
          <button onClick={deleteResource} className="px-3 py-1 rounded border border-red-300 text-red-600 text-xs hover:bg-red-50">Delete</button>
        )}
      </div>

      {error && <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}

      {/* ── Header Fields (SAP-style 2-column form) ── */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 mb-3">
        <div className="grid grid-cols-[140px_1fr_120px_1fr] gap-x-4 gap-y-2 items-center">
          {/* Row 1: Resource No + Bar Code */}
          <label className={fieldLabel}>Resource No.</label>
          <input value={form.resource_no} onChange={e => set('resource_no', e.target.value)}
            readOnly={!isNew} className={`${fieldInput} ${!isNew ? 'bg-slate-50' : ''}`} />
          <label className={fieldLabel}>Bar Code</label>
          <input value={form.bar_code} onChange={e => set('bar_code', e.target.value)} className={fieldInput} />

          {/* Row 2: Description (full width) */}
          <label className={fieldLabel}>Description</label>
          <input value={form.description} onChange={e => set('description', e.target.value)}
            className={fieldInput + ' col-span-3'} />

          {/* Row 3: Foreign Name (full width) */}
          <label className={fieldLabel}>Foreign Name</label>
          <input value={form.foreign_name} onChange={e => set('foreign_name', e.target.value)}
            className={fieldInput + ' col-span-3'} />

          {/* Row 4: Resource Type + (empty right) */}
          <label className={fieldLabel}>Resource Type</label>
          <select value={form.resource_type} onChange={e => set('resource_type', e.target.value)} className={fieldSelect}>
            {TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
          </select>
          <div className="col-span-2" />

          {/* Row 5: Resource Group */}
          <label className={fieldLabel}>Resource Group</label>
          <select value={form.resource_group} onChange={e => set('resource_group', e.target.value)} className={fieldSelect}>
            {GROUP_OPTIONS.map(g => <option key={g}>{g}</option>)}
          </select>
          <div className="col-span-2" />

          {/* Row 6: Unit of Measure Text */}
          <label className={fieldLabel}>Unit of Measure Text</label>
          <input value={form.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)} className={fieldInput} />
          <div className="col-span-2" />

          {/* Row 7: Time per Resource Units + Res. Units per Time Period */}
          <label className={fieldLabel}>Time per Resource Units</label>
          <input value={form.time_per_unit} onChange={e => set('time_per_unit', e.target.value)}
            placeholder="0:00:00" className={fieldInput} />
          <label className={fieldLabel}>Res. Units per Time Period</label>
          <input type="number" min="1" value={form.units_per_period}
            onChange={e => set('units_per_period', e.target.value)} className={fieldInput} />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-0 border-b border-slate-300 mb-0">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium border border-b-0 rounded-t transition-colors
              ${tab === t.key
                ? 'bg-white border-slate-300 text-sap-blueDeeper -mb-px z-10'
                : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="bg-white rounded-b-lg border border-t-0 border-slate-300 shadow-sm p-4 min-h-[260px]">
        {/* General Tab */}
        {tab === 'general' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Left: Issue Method, Resource Allocation, Linked to Item */}
            <div className="space-y-3">
              <div className="grid grid-cols-[130px_1fr] gap-x-3 items-center">
                <label className={fieldLabel}>Issue Method</label>
                <select value={form.issue_method} onChange={e => set('issue_method', e.target.value)} className={fieldSelect}>
                  {ISSUE_OPTIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-x-3 items-center">
                <label className={fieldLabel}>Resource Allocation</label>
                <select value={form.resource_allocation} onChange={e => set('resource_allocation', e.target.value)} className={fieldSelect}>
                  {ALLOC_OPTIONS.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-x-3 items-center">
                <label className={fieldLabel}>Linked to Item</label>
                <input value={form.linked_to_item} onChange={e => set('linked_to_item', e.target.value)} className={fieldInput} />
              </div>
            </div>

            {/* Right: Resource Std Cost table */}
            <div>
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-600 border-b border-slate-200">Resource Std Cost</th>
                    <th className="text-right px-3 py-1.5 font-semibold text-slate-600 border-b border-slate-200">Default Std Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map((c, i) => (
                    <tr key={c.cost_number} className="border-b border-slate-100">
                      <td className="px-3 py-1 text-slate-500 text-xs">Cost {c.cost_number}</td>
                      <td className="px-1 py-0.5">
                        <input type="number" step="0.01" value={c.cost_amount}
                          onChange={e => {
                            const updated = [...costs];
                            updated[i] = { ...updated[i], cost_amount: Number(e.target.value) || 0 };
                            setCosts(updated);
                          }}
                          className="w-full px-2 py-1 rounded border border-slate-200 text-sm text-right focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center mt-1 px-3 py-1.5 bg-slate-50 border border-t-0 border-slate-200 rounded-b">
                <span className="text-xs font-semibold text-slate-600">Total Std Resource Cost</span>
                <span className="text-sm font-bold text-[#1A3C34]">₹{totalStdCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        )}

        {/* Capacity Data Tab */}
        {tab === 'planning' && (
          <div>
            {/* Daily Capacity Factors header */}
            <table className="w-full text-sm border border-slate-200 mb-4">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-200 px-3 py-1.5" colSpan={1}></th>
                  <th className="border border-slate-200 px-3 py-1.5 text-center font-semibold text-slate-600" colSpan={4}>Daily Capacity Factors</th>
                </tr>
                <tr className="bg-slate-50">
                  <th className="border border-slate-200 px-3 py-1.5"></th>
                  {[1,2,3,4].map(n => (
                    <th key={n} className="border border-slate-200 px-3 py-1.5 text-center font-medium text-slate-600 w-16">{n}</th>
                  ))}
                </tr>
              </thead>
            </table>

            {/* Standard Daily Capacity + Days */}
            <table className="w-full text-sm border border-slate-200">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-700">Standard Daily Capacity</th>
                  {[1,2,3,4].map(n => (
                    <th key={n} className="border border-slate-200 px-2 py-1.5 text-center w-16">
                      <input type="text" value={stdCapacity[`f${n}`]}
                        onChange={e => setStdCapacity(s => ({ ...s, [`f${n}`]: e.target.value }))}
                        className="w-full text-center text-sm border border-slate-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
                    </th>
                  ))}
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-700">Daily Capacity</th>
                </tr>
              </thead>
              <tbody>
                {capacity.map((row, i) => (
                  <tr key={row.day} className={i % 2 === 0 ? 'bg-blue-50/40' : 'bg-white'}>
                    <td className="border border-slate-200 px-3 py-1.5 font-medium text-slate-700">{row.day}</td>
                    {[1,2,3,4].map(n => (
                      <td key={n} className="border border-slate-200 px-1 py-0.5">
                        <input type="text" value={row[`f${n}`]}
                          onChange={e => {
                            const updated = [...capacity];
                            updated[i] = { ...updated[i], [`f${n}`]: e.target.value };
                            setCapacity(updated);
                          }}
                          className="w-full text-center text-sm border border-slate-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
                      </td>
                    ))}
                    <td className="border border-slate-200 px-1 py-0.5">
                      <input type="text" value={row.daily}
                        onChange={e => {
                          const updated = [...capacity];
                          updated[i] = { ...updated[i], daily: e.target.value };
                          setCapacity(updated);
                        }}
                        className="w-full text-sm border border-slate-200 rounded px-2 py-0.5 focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Planning Data Tab */}
        {tab === 'capacity' && (
          <div>
            {/* Capacity Period row */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-semibold text-slate-600">Capacity Period</span>
              <button className="px-2 py-0.5 text-xs rounded border border-slate-300 text-slate-500 hover:bg-slate-50">&lt;&lt;</button>
              <span className="text-sm text-slate-600">From</span>
              <input type="date" value={capacityFrom} onChange={e => setCapacityFrom(e.target.value)}
                className="px-2 py-1 text-sm rounded border border-slate-300 focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
              <span className="text-sm text-slate-600">To</span>
              <input type="date" value={capacityTo} onChange={e => setCapacityTo(e.target.value)}
                className="px-2 py-1 text-sm rounded border border-slate-300 focus:ring-1 focus:ring-sap-blue/40 focus:outline-none" />
              <button className="px-2 py-0.5 text-xs rounded border border-slate-300 text-slate-500 hover:bg-slate-50">&gt;&gt;</button>
            </div>

            {/* Warehouse table */}
            <table className="w-full text-sm border border-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-8">#</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600">Whse Code</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600">Whse Name</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-20">Locked</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-20">Internal</th>
                </tr>
              </thead>
              <tbody>
                {warehouses.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-400 italic text-sm">No warehouse data</td></tr>
                ) : warehouses.map((wh, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-1.5">{wh.code}</td>
                    <td className="px-3 py-1.5">{wh.name}</td>
                    <td className="px-3 py-1.5">{wh.locked ? 'Y' : 'N'}</td>
                    <td className="px-3 py-1.5">{wh.internal ? 'Y' : 'N'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end mt-3">
              <button className="px-4 py-1.5 rounded bg-slate-500 text-white text-sm font-medium hover:bg-slate-600">
                Set Default Whse
              </button>
            </div>
          </div>
        )}

        {/* Fixed Assets Tab */}
        {tab === 'fixedAssets' && (
          <table className="w-full text-sm border border-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-8">#</th>
                <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-red-600">Fixed Asset Item No.</th>
                <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-red-600">Fixed Asset Item Name</th>
              </tr>
            </thead>
            <tbody>
              {fixedAssets.length === 0 ? (
                <tr><td colSpan={3} className="text-center py-16 text-slate-400 italic text-sm">No fixed assets linked</td></tr>
              ) : fixedAssets.map((fa, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                  <td className="px-3 py-1.5">{fa.item_no}</td>
                  <td className="px-3 py-1.5">{fa.item_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Properties Tab */}
        {tab === 'properties' && (
          <div className="flex gap-4">
            <table className="flex-1 text-sm border border-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-8">#</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600">Property Name</th>
                </tr>
              </thead>
              <tbody>
                {properties.length === 0 ? (
                  <tr><td colSpan={2} className="text-center py-16 text-slate-400 italic text-sm">No properties defined</td></tr>
                ) : properties.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-1.5">{p.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-col gap-2">
              <button onClick={() => {
                const allProps = Array.from({ length: 10 }, (_, i) => ({ name: `Property ${i + 1}` }));
                setProperties(allProps);
              }} className="px-4 py-1.5 rounded bg-slate-500 text-white text-sm font-medium hover:bg-slate-600 whitespace-nowrap">
                Select All
              </button>
              <button onClick={() => setProperties([])}
                className="px-4 py-1.5 rounded bg-slate-500 text-white text-sm font-medium hover:bg-slate-600 whitespace-nowrap">
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Attachments Tab */}
        {tab === 'attachments' && (
          <div className="flex gap-4">
            <table className="flex-1 text-sm border border-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-slate-600 w-8">#</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-red-600">Target Path</th>
                  <th className="border border-slate-200 px-3 py-1.5 text-left font-semibold text-red-600">File Name</th>
                </tr>
              </thead>
              <tbody>
                {attachments.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-16 text-slate-400 italic text-sm">No attachments</td></tr>
                ) : attachments.map((a, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-1.5">{a.path}</td>
                    <td className="px-3 py-1.5">{a.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-col gap-2">
              <label className="px-4 py-1.5 rounded bg-slate-400 text-white text-sm font-medium hover:bg-slate-500 cursor-pointer text-center">
                Browse
                <input type="file" className="hidden" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) setAttachments(prev => [...prev, { path: file.name, name: file.name, file }]);
                  e.target.value = '';
                }} />
              </label>
              <button className="px-4 py-1.5 rounded bg-slate-400 text-white text-sm font-medium hover:bg-slate-500">Display</button>
              <button onClick={() => setAttachments(prev => prev.slice(0, -1))}
                className="px-4 py-1.5 rounded bg-slate-400 text-white text-sm font-medium hover:bg-slate-500 mt-4">Delete</button>
            </div>
          </div>
        )}

        {/* Remarks Tab */}
        {tab === 'remarks' && (
          <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)}
            rows={12} placeholder="Enter remarks…"
            className="w-full h-full min-h-[240px] px-3 py-2 rounded border border-slate-300 text-sm focus:ring-1 focus:ring-sap-blue/40 focus:outline-none resize-none" />
        )}
      </div>

      {/* ── Status Radio + Action Buttons (SAP-style bottom bar) ── */}
      <div className="flex items-center justify-between mt-4">
        {/* Status radio buttons */}
        <div className="flex items-center gap-4">
          {['Active', 'Inactive'].map(s => (
            <label key={s} className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700">
              <input type="radio" name="status" checked={form.status === s}
                onChange={() => set('status', s)}
                className="accent-sap-blue" />
              {s}
            </label>
          ))}
        </div>
        {/* Action buttons */}
        <div className="flex gap-2">
          <button onClick={() => navigate('/resources/master')}
            className="px-5 py-2 rounded border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50">
            {isNew ? 'Cancel' : 'Find'}
          </button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 rounded bg-sap-blue text-white text-sm font-medium hover:bg-sap-blueDark disabled:opacity-50">
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}
